import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { WorkshopDto } from '@workshop-hst/shared';
import { api, ApiError } from '../lib/api';
import { useToast } from './Toast';

/**
 * Shows the Zoom meeting status for a workshop. Provisioning happens at create
 * time — this panel is the admin's confirmation that it succeeded, and a Retry
 * lane if it didn't.
 */
export function ZoomPanel({ workshop }: { workshop: WorkshopDto }) {
  const qc = useQueryClient();
  const toast = useToast();
  const provisioned = Boolean(workshop.zoomJoinUrl);
  const externallyManaged = workshop.zoomManagedExternally;
  const [showAdopt, setShowAdopt] = useState(false);
  const [joinUrl, setJoinUrl] = useState('');

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'workshop', workshop.id] });
    qc.invalidateQueries({ queryKey: ['admin', 'workshops'] });
  };

  const regen = useMutation({
    mutationFn: () => api.admin.regenerateZoomMeeting(workshop.id),
    onSuccess: () => {
      toast.success('Zoom meeting ready', 'Shared meeting URL refreshed.');
      refresh();
    },
    onError: (err) =>
      toast.error('Zoom retry failed', err instanceof ApiError ? err.message : String(err)),
  });

  const adopt = useMutation({
    mutationFn: () => api.admin.adoptZoomMeeting(workshop.id, joinUrl.trim()),
    onSuccess: () => {
      toast.success(
        'External Zoom meeting connected',
        'Portal attendees now use the externally managed meeting.',
      );
      setJoinUrl('');
      setShowAdopt(false);
      refresh();
    },
    onError: (err) =>
      toast.error('Zoom link rejected', err instanceof ApiError ? err.message : String(err)),
  });

  const copyUrl = async (url: string, label: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.info('Copied', `${label} copied to clipboard.`);
    } catch {
      // Fallback — show the URL so the user can copy manually.
      window.prompt('Copy this URL:', url);
    }
  };

  const adoptForm = showAdopt ? (
    <form
      className="mt-4 rounded-xl border border-line bg-canvas p-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!joinUrl.trim()) return;
        if (
          confirm(
            'Use this Zoom meeting? Existing portal attendee redirects and future recordings will move to it. The old Zoom meeting will not be deleted.',
          )
        ) {
          adopt.mutate();
        }
      }}
    >
      <label className="text-xs font-semibold text-ink" htmlFor={`zoom-join-url-${workshop.id}`}>
        External Zoom attendee link
      </label>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
        <input
          id={`zoom-join-url-${workshop.id}`}
          type="url"
          required
          value={joinUrl}
          onChange={(event) => setJoinUrl(event.target.value)}
          placeholder="https://us06web.zoom.us/j/123456789?pwd=…"
          className="input min-w-0 flex-1 font-mono text-xs"
        />
        <button type="submit" className="btn-accent shrink-0" disabled={adopt.isPending}>
          {adopt.isPending ? 'Verifying…' : 'Verify and use external meeting'}
        </button>
        <button
          type="button"
          className="btn-secondary shrink-0"
          disabled={adopt.isPending}
          onClick={() => {
            setShowAdopt(false);
            setJoinUrl('');
          }}
        >
          Cancel
        </button>
      </div>
      <p className="mt-2 text-[11px] text-ink-muted">
        Use a meeting created outside Lenny’s Workshops, such as a Maven-managed meeting. It must
        belong to the connected Zoom account. Existing calendar links stay the same because their
        redirects are repointed automatically. Lenny’s will not change or delete the external
        meeting.
      </p>
    </form>
  ) : null;

  if (!provisioned) {
    return (
      <section className="rounded-2xl border border-amber-300 bg-amber-50/60 p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-900">
              <Cross /> Zoom meeting not provisioned
            </h3>
            <p className="mt-1 text-xs text-amber-900/80">
              The Zoom meeting could not be created when this workshop was saved (likely a
              credential or network issue). The workshop can't be published until this succeeds.
              Click Retry to try again.
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              className="btn-secondary"
              disabled={adopt.isPending}
              onClick={() => setShowAdopt((value) => !value)}
            >
              Use external meeting
            </button>
            <button
              type="button"
              className="btn-accent"
              disabled={regen.isPending}
              onClick={() => regen.mutate()}
            >
              {regen.isPending ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        </div>
        {adoptForm}
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-line bg-surface p-5 shadow-card">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
          <Check /> {externallyManaged ? 'External Zoom meeting connected' : 'Zoom meeting ready'}
        </h3>
        <span
          className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${
            externallyManaged ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'
          }`}
        >
          {externallyManaged ? 'Managed by Maven' : 'Created by Lenny’s'}
        </span>
      </div>

      {externallyManaged ? (
        <p className="mt-2 max-w-2xl text-xs leading-relaxed text-ink-muted">
          Portal RSVPs redirect to this Maven-managed meeting. Lenny’s Workshops does not change
          or delete its Zoom settings.
        </p>
      ) : (
        <p className="mt-2 max-w-2xl text-xs leading-relaxed text-ink-muted">
          This meeting was created by Lenny’s Workshops. Its attendee link can also be used on
          external platforms.
        </p>
      )}

      {workshop.zoomJoinUrl ? (
        <div className="mt-5">
          <p className="text-xs font-semibold text-ink">
            {externallyManaged ? 'Maven attendee link' : 'Lenny’s attendee link'}
          </p>
          <div className="mt-2 flex w-full items-center gap-2 rounded-lg border border-line bg-canvas px-3 py-2 font-mono text-[12px] text-ink-muted">
            <span className="min-w-0 flex-1 truncate">{workshop.zoomJoinUrl}</span>
            <button
              type="button"
              onClick={() =>
                copyUrl(
                  workshop.zoomJoinUrl!,
                  externallyManaged ? 'Maven attendee link' : 'Lenny’s attendee link',
                )
              }
              className="shrink-0 rounded px-2 py-0.5 text-[11px] font-semibold text-ink hover:bg-surface"
              title={externallyManaged ? 'Copy Maven attendee link' : 'Copy Lenny’s attendee link'}
            >
              Copy
            </button>
            <a
              href={workshop.zoomJoinUrl}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 rounded px-2 py-0.5 text-[11px] font-semibold text-accent hover:underline"
            >
              Open ↗
            </a>
          </div>
        </div>
      ) : null}

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
        <p className="text-[11px] text-ink-subtle">Need to change which Zoom meeting is connected?</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-secondary"
            disabled={adopt.isPending}
            onClick={() => setShowAdopt((value) => !value)}
          >
            {externallyManaged ? 'Change Maven link' : 'Use external meeting'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={regen.isPending}
            onClick={() => {
              const message = externallyManaged
                ? 'Replace the external meeting with a new meeting created by Lenny’s Workshops? The external meeting will be left unchanged, but portal redirects will move to the new Lenny’s link.'
                : 'Regenerate the Zoom meeting? The old shared link will stop working, so you will need to replace it in Maven and any other external listings.';
              if (confirm(message)) {
                regen.mutate();
              }
            }}
          >
            {regen.isPending
              ? externallyManaged
                ? 'Replacing…'
                : 'Regenerating…'
              : externallyManaged
                ? 'Switch to Lenny’s meeting'
                : 'Regenerate meeting'}
          </button>
        </div>
      </div>
      {adoptForm}
    </section>
  );
}

function Check() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <circle cx="7" cy="7" r="7" fill="#0B5132" />
      <path d="M4 7l2 2 4-4" stroke="#fff" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Cross() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <circle cx="7" cy="7" r="7" fill="#B7791F" />
      <path d="M4.5 4.5l5 5M9.5 4.5l-5 5" stroke="#fff" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}
